import { Box, Button, Typography } from '@mui/material';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { useTranslation } from 'react-i18next';
import { memo, useEffect } from 'react';
import { handleApiError } from '~/utils/helper';
import dayjs from 'dayjs';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { TypeEnum } from '~/utils/enum';
import { handleGetListActionLog } from '~/thunks/action-log/actionLogThunk';
import { actionLogActions } from '~/thunks/action-log/actionLogSlice';
import ActionLogList from './ActionLogList';
import { ArrowLeft2Icon, ArrowRight3Icon } from '~/assets/icons';
import { useNavigate } from 'react-router-dom';
import { adminRouteAbsolute } from '~/utils/constants/route';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import { PAGE_INDEX } from '~/utils/constants/common';

const ActionLog = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/action-log');

  const { filter, needRefreshData } = useAppSelector((state) => state.actionLog);
  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const username = selectedUser?.username ?? userProfile?.username;

  const typeOptions = [
    {
      id: TypeEnum.TAI_KHOAN,
      name: t('type.' + TypeEnum.TAI_KHOAN),
    },
    {
      id: TypeEnum.HO_SO,
      name: t('type.' + TypeEnum.HO_SO),
    },
    {
      id: TypeEnum.THIET_BI,
      name: t('type.' + TypeEnum.THIET_BI),
    },
    {
      id: TypeEnum.HANG_RAO,
      name: t('type.' + TypeEnum.HANG_RAO),
    },
    {
      id: TypeEnum.CANH_BAO,
      name: t('type.' + TypeEnum.CANH_BAO),
    },
  ];

  useEffect(() => {
    if (!username) return;

    dispatch(handleGetListActionLog({ ...filter, username: username }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData, username]);

  // Reset when change login
  useEffect(() => {
    return () => {
      dispatch(actionLogActions.resetFilter());
    };
  }, [dispatch]);

  const handleClearFilter = () => {
    dispatch(actionLogActions.resetFilter());
  };

  return (
    <Box className='w-full'>
      <Box className='flex bg-[#123B6D] p-5 w-full mb-3 items-center'>
        <ArrowLeft2Icon
          stroke='#fff'
          className='w-4 ml-8 mr-4 hover:cursor-pointer'
          onClick={() => navigate('/admin/dashboard')}
        />
        <Typography
          className='text-white font-semibold text-xl hover:cursor-pointer'
          onClick={() => navigate(`${adminRouteAbsolute.profile}`)}
        >
          {t('profile')}
        </Typography>
        <ArrowRight3Icon className='w-4 h-4 mx-2' stroke='#fff' />
        <Typography className='text-white font-semibold text-xl'>{t('title')}</Typography>
      </Box>

      <BaseRow className='gap-4 px-6 pt-6 mb-7 2xl:!items-end max-md:!flex-col'>
        <Box className='w-full'>
          <Typography className='text-[#161A1F] font-semibold text-sm mb-2'>{t('type.title')}</Typography>
          <BaseSelect
            options={typeOptions}
            value={filter.type ?? ''}
            placeholder={t('placeholder.type')}
            isDisplayAllOption
            onChange={(e) => {
              const value = e.target.value;
              dispatch(
                actionLogActions.setFilter({ type: value === '' ? undefined : Number(value), pageIndex: PAGE_INDEX })
              );
            }}
          />
        </Box>
        <Box className='w-full'>
          <Typography className='text-[#161A1F] font-semibold text-sm mb-2'>{t('action_time')}</Typography>
          <Box className='flex justify-between items-center gap-3 max-[768px]:!flex-col'>
            <Box className='w-full'>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={filter.startDate}
                dateFormat='YYYY-MM-DD'
                onChange={(date) =>
                  dispatch(
                    actionLogActions.setFilter({
                      startDate: dayjs(date).startOf('day').format('YYYY-MM-DD'),
                      pageIndex: PAGE_INDEX,
                    })
                  )
                }
                placeholder={t('startDate', { ns: 'admin/common' })}
                calendarProps={filter.endDate ? { maxDate: dayjs(filter.endDate).toDate(), pageIndex: PAGE_INDEX } : {}}
                className='w-full'
              />
            </Box>
            <Box className='w-4 h-px bg-black hidden lg:block'></Box>
            <Box className='w-full'>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={filter.endDate}
                dateFormat='YYYY-MM-DD'
                onChange={(date) =>
                  dispatch(
                    actionLogActions.setFilter({
                      endDate: dayjs(date).endOf('day').format('YYYY-MM-DD'),
                      pageIndex: PAGE_INDEX,
                    })
                  )
                }
                placeholder={t('endDate', { ns: 'admin/common' })}
                calendarProps={
                  filter.startDate ? { minDate: dayjs(filter.startDate).toDate(), pageIndex: PAGE_INDEX } : {}
                }
                className='w-full'
              />
            </Box>
            <Box className='flex-1'>
              <Button
                className='border border-solid border-[#123B6D] bg-[#DCE8F5] rounded-xl text-base font-bold text-[#123B6D] w-[132px] hover:bg-[#0E2F58] hover:opacity-80 hover:text-white'
                onClick={handleClearFilter}
              >
                {t('clear', { ns: 'admin/common' })}
              </Button>
            </Box>
          </Box>
        </Box>
      </BaseRow>
      <Box className='px-6'>
        <ActionLogList />
      </Box>
    </Box>
  );
};

export default memo(ActionLog);
